import { DialogButton } from '@beeinventor/dasiot-react-component-lib';
import { Box, CircularProgress, InputBase, styled } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getDasEndpoints } from '../../../apis/dasEndpointApi';
import EmptyDataWarning from '../../../components/EmptyDataWarning';
import ArrowLeftSvgIcon from '../../../components/SvgIcon/ArrowLeftSvgIcon';
import SearchSvgIconThin from '../../../components/SvgIcon/SearchSvgIconThin';
import { DasEndpoint } from '../../../types/Device';
import DeviceItem from './DeviceItem';
import { useInfiniteQuery } from '@tanstack/react-query';
import useDebounce from '../../../hooks/useDebounce';

const Container = styled('div')`
  padding: 24px;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 200px); // Dynamic height based on viewport
  min-height: 400px; // Minimum height to ensure usability
  background-color: #fff;
  color: black;
`;

const LoadingWrapper = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`;

const ButtonWrapper = styled('div')`
  flex-shrink: 0;
  margin-bottom: 16px;
`;

const ScrollableBox = styled(Box)`
  flex: 1;
  overflow-y: auto;
  min-height: 470px;
  padding-right: 8px;
`;

const SearchContainer = styled('div')`
  display: flex;
  align-items: center;

  border-radius: 4px;
  padding: 8px;
  height: 32px;
  border-radius: 4px;
  background: #fff;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  font-family: 'Noto Sans';
  font-size: 12px;
  font-style: normal;

  & > input {
    flex: 1;
    border: none;
    background: none;
    outline: none;

    font-family: 'Noto Sans';
    font-size: 12px;
    font-style: normal;
    &::placeholder {
      color: #757575;
    }
  }
`;

interface SelectDeviceListProps {
  onBack?: () => void;
  onDevicesSelect?: (
    devices: {
      value: string;
      isChecked: boolean;
      endpointId: string;
      device: DasEndpoint | undefined;
    }[],
  ) => void;
  initialSelectedDevices?: Set<string>;
  initialSelectedDeviceObjects?: DasEndpoint[];
  selectedProjectId: string | undefined;
}

const SelectDeviceList: React.FC<SelectDeviceListProps> = ({
  onBack,
  onDevicesSelect,
  initialSelectedDevices = new Set(),
  initialSelectedDeviceObjects = [],
  selectedProjectId,
}) => {
  const [searchText, setSearchText] = useState('');
  const [selectedDevices, setSelectedDevices] = useState<Set<string>>(
    initialSelectedDevices,
  );
  const selectedDeviceMap = useRef<Map<string, DasEndpoint | undefined>>(
    new Map(initialSelectedDeviceObjects.map((d) => [d.id, d])),
  );
  const listRef = useRef<HTMLDivElement>(null);
  const querySearch = useDebounce(searchText, 1000);

  const {
    data: getEndpointsResponse,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
  } = useInfiniteQuery({
    queryKey: ['get-dasendpoints-device', selectedProjectId, querySearch],
    queryFn: async ({ pageParam }: { pageParam?: string }) => {
      const res = await getDasEndpoints({
        type: 'dasloop',
        nextCursor: pageParam,
        limit: 10,
        extendDataPointShadow: true,
        projectId: selectedProjectId,
        dasId: querySearch || undefined,
      });
      return res;
    },
    getNextPageParam: (res) => res.data.paging.nextCursor,
    refetchOnWindowFocus: false,
    initialPageParam: undefined,
    enabled: location.pathname === '/task-list',
  });

  const dasEndpoints = useMemo(() => {
    return getEndpointsResponse?.pages.map((res) => res.data.data).flat();
  }, [getEndpointsResponse]);

  useEffect(() => {
    const lastItemDom = document.querySelector('#last-item');
    if (listRef.current && lastItemDom && hasNextPage) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            fetchNextPage();
          }
        },
        {
          root: listRef.current,
        },
      );

      observer.observe(lastItemDom);

      return () => {
        observer.disconnect();
      };
    }
  }, [dasEndpoints, hasNextPage]);

  const handleDeviceSelect = (device: DasEndpoint, checked: boolean) => {
    if (checked) {
      selectedDeviceMap.current.set(device.id, device);
    } else {
      selectedDeviceMap.current.delete(device.id);
    }

    setSelectedDevices((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(device.id);
      } else {
        newSet.delete(device.id);
      }

      const selectedList = Array.from(newSet).map((id) => {
        const d = selectedDeviceMap.current.get(id);
        return {
          value: d?.dasId ?? '',
          isChecked: true,
          endpointId: id,
          device: d,
        };
      });
      onDevicesSelect?.(selectedList);

      return newSet;
    });
  };

  return (
    <Container>
      <ButtonWrapper>
        <DialogButton
          mode="light"
          color="info"
          variant="text"
          startIcon={<ArrowLeftSvgIcon />}
          onClick={onBack}
        >
          Back
        </DialogButton>
      </ButtonWrapper>
      <Box sx={{ padding: '8px', background: '#f3f3f3' }}>
        <SearchContainer>
          <InputBase
            placeholder="Search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            fullWidth
            startAdornment={
              <SearchSvgIconThin
                sx={{ width: '32px', height: '32px', mr: 'e' }}
              />
            }
            inputProps={{ sx: { fontSize: '12px' } }}
          />
        </SearchContainer>
      </Box>
      <ScrollableBox ref={listRef}>
        {(dasEndpoints?.length ?? 0) > 0 ? (
          <>
            {dasEndpoints?.map((device, index) => (
              <DeviceItem
                id={index === dasEndpoints.length - 1 ? 'last-item' : undefined}
                key={device.id}
                deviceItem={device}
                onSelect={handleDeviceSelect}
                isSelected={selectedDevices.has(device.id)}
                disabled={
                  selectedDevices.size >= 10 && !selectedDevices.has(device.id)
                }
              />
            ))}
          </>
        ) : querySearch ? (
          <EmptyDataWarning mode="light" />
        ) : (
          <EmptyDataWarning mode="light" />
        )}
        {(isFetching || isFetchingNextPage) && (
          <LoadingWrapper>
            <CircularProgress size={24} />
          </LoadingWrapper>
        )}
      </ScrollableBox>
    </Container>
  );
};

export default SelectDeviceList;
